import React, { useState, useEffect } from 'react';
import { Trophy, Users, TrendingUp, Lightbulb, UserPlus, Loader2, Medal, Crown, Award } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaderboardCard = ({ user, type }) => {
  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-300" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return <span className="text-gray-500 font-bold">#{rank}</span>;
  };

  const getBadgeColor = (badge) => {
    if (badge === 'invited') return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    return 'bg-[#3B82F6]/20 text-[#3B82F6] border-[#3B82F6]/30';
  };

  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
      user.rank <= 3 ? 'bg-gradient-to-r from-[#1e2a45]/80 to-[#131b2e] border border-[#3B82F6]/30' : 'bg-[#131b2e] border border-[#1e2a45]'
    }`}>
      {/* Rank */}
      <div className="w-10 flex justify-center">
        {getRankIcon(user.rank)}
      </div>

      {/* Avatar */}
      <div className="relative">
        {user.picture ? (
          <img 
            src={user.picture} 
            alt={user.display_name}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
            <span className="text-[#3B82F6] font-bold text-lg">
              {user.display_name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
        )}
        {user.rank === 1 && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center">
            <Crown className="h-2.5 w-2.5 text-yellow-900" />
          </div>
        )}
      </div>

      {/* User Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-white truncate">{user.display_name}</span>
          {user.badges?.map(badge => (
            <span key={badge} className={`text-xs px-1.5 py-0.5 rounded border ${getBadgeColor(badge)}`}>
              {badge}
            </span>
          ))}
        </div>
        
        {/* Stats based on type */}
        {type === 'traders' && (
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
            <span>{user.total_trades} trades</span>
            <span className="text-green-400">${user.total_volume?.toLocaleString()}</span>
          </div>
        )}
        {type === 'creators' && (
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
            <span>{user.total_ideas} ideas</span>
            <span className="text-green-400">{user.live_ideas} live</span>
            <span className="text-[#3B82F6]">{user.total_votes} votes</span>
          </div>
        )}
        {type === 'inviters' && (
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
            <span>{user.total_invites} friends invited</span>
            <span className="text-gray-400">{user.links_created} links</span>
          </div>
        )}
      </div>

      {/* Score/Volume */}
      <div className="text-right">
        {type === 'traders' && (
          <span className="text-lg font-bold text-green-400">${user.total_volume?.toLocaleString()}</span>
        )}
        {type === 'creators' && (
          <span className="text-lg font-bold text-[#3B82F6]">{user.score} pts</span>
        )}
        {type === 'inviters' && (
          <span className="text-lg font-bold text-purple-400">{user.total_invites}</span>
        )}
      </div>
    </div>
  );
};

const LeaderboardPage = () => {
  const [activeTab, setActiveTab] = useState('traders');
  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(true);
  const [traders, setTraders] = useState([]);
  const [creators, setCreators] = useState([]);
  const [inviters, setInviters] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tradersRes, creatorsRes, invitersRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/leaderboard/traders?period=${period}&limit=20`).then(r => r.json()),
        fetch(`${API_URL}/api/leaderboard/idea-creators?limit=20`).then(r => r.json()),
        fetch(`${API_URL}/api/leaderboard/inviters?limit=20`).then(r => r.json()),
        fetch(`${API_URL}/api/leaderboard/summary`).then(r => r.json())
      ]);
      
      setTraders(tradersRes.leaderboard || []);
      setCreators(creatorsRes.leaderboard || []);
      setInviters(invitersRes.leaderboard || []);
      setSummary(summaryRes);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'traders', name: 'Top Traders', icon: TrendingUp, count: traders.length },
    { id: 'creators', name: 'Idea Creators', icon: Lightbulb, count: creators.length },
    { id: 'inviters', name: 'Top Inviters', icon: UserPlus, count: inviters.length }
  ];

  const periods = [
    { id: 'day', name: '24h' },
    { id: 'week', name: '7 Days' },
    { id: 'month', name: '30 Days' },
    { id: 'all', name: 'All Time' }
  ];

  const getCurrentLeaderboard = () => {
    if (activeTab === 'traders') return traders;
    if (activeTab === 'creators') return creators;
    if (activeTab === 'inviters') return inviters;
    return [];
  };

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO
        title="Leaderboard — Top Traders, Idea Creators & Inviters"
        description="See the top traders, market idea creators, and top inviters on AMP Predict. Weekly, monthly, and all-time rankings for the community."
        keywords="prediction market leaderboard, top traders, idea creators, prediction community, AMP Predict rankings"
        url="https://amppredict.com/leaderboard"
      />
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-[#131b2e] to-[#050810] border-b border-[#1e2a45]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#F97316] to-[#EA580C] mb-4">
              <Trophy className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Leaderboard</h1>
            <p className="text-gray-400 max-w-xl mx-auto">
              See who's leading the prediction market. Top traders, idea creators, and community builders.
            </p>
          </div>

          {/* Stats Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45] text-center">
                <Users className="h-5 w-5 text-[#3B82F6] mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{summary.total_users?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Total Users</div>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45] text-center">
                <TrendingUp className="h-5 w-5 text-green-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{summary.total_trades?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Total Trades</div>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45] text-center">
                <Lightbulb className="h-5 w-5 text-[#F97316] mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{summary.total_ideas?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Ideas Submitted</div>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45] text-center">
                <Award className="h-5 w-5 text-purple-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{summary.live_markets?.toLocaleString()}</div>
                <div className="text-xs text-gray-500">Live Markets</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs & Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-[#3B82F6] text-white'
                  : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.name}
              {tab.count > 0 && (
                <span className={`px-1.5 py-0.5 text-xs rounded ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-[#3B82F6]/20 text-[#3B82F6]'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Period Filter (only for traders) */}
        {activeTab === 'traders' && (
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-gray-500">Period:</span>
            {periods.map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                  period === p.id
                    ? 'bg-[#F97316] text-white'
                    : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        {/* Leaderboard List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
            <span className="ml-3 text-gray-400">Loading leaderboard...</span>
          </div>
        ) : getCurrentLeaderboard().length === 0 ? (
          <div className="text-center py-20">
            <Trophy className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No data yet</h3>
            <p className="text-gray-400">
              {activeTab === 'traders' && 'Start trading to appear on the leaderboard!'}
              {activeTab === 'creators' && 'Submit ideas to climb the ranks!'}
              {activeTab === 'inviters' && 'Invite friends to earn your spot!'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {getCurrentLeaderboard().map(user => (
              <LeaderboardCard key={user.user_id} user={user} type={activeTab} />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default LeaderboardPage;
